import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { IMqttMessage, MqttService } from 'ngx-mqtt';
import { NotificationRingComponent } from 'src/app/modules/feature/alarms/components/notifications/notification-ring/notification-ring.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { BuildingDropdownComponent } from './building-dropdown/building-dropdown.component';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { UserService } from 'src/app/services/api/system-settings/user/user.service';
import { CommonApiResponse } from 'src/app/models/common-api-response.model';
import { LoggedInUserinfo } from 'src/app/models/user.model';
import { HeaderService } from '../../services/header.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports:[CommonModule,RouterModule, NotificationRingComponent,BuildingDropdownComponent,NgbDropdownModule,NgbTooltipModule],
  providers: [
    { provide: Window, useValue: window },
    UserService
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('alarmPopupHost', { read: ViewContainerRef }) private alarmPopupHost?: ViewContainerRef;
  private alarmPopupComponent?: { open(notification: any): void };
  private notificationSubscription?: any;
  private notificationContactId: string | null = null;
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();
  public loggedInUserinfo: LoggedInUserinfo;
  public avatar: string | null = null;
  public avatarLoadFailed: boolean = false;
  public isDark: boolean = false;
  private isMfaModalOpen: boolean = false;
  constructor(
    private _authService: AuthService,
    private _mqttService: MqttService,
    private _userService: UserService,
    private _el: ElementRef,
    private _renderer: Renderer2,
    private _headerService: HeaderService,
    private _router: Router,
    @Inject(DOCUMENT) private document,
  ) {
    this.isDark = localStorage.getItem('isDark') === 'true';
    this.setTheme();
  }

  ngOnInit(): void {
    this.getUserInfo();
    // TEST: Uncomment to show test popup on load
    // setTimeout(() => this.showTestNotification(), 500);
    
    // Subscribe to user info changes from the service
    this._headerService.getUserInfoValue().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(user => {
      if (user) {
        this.loggedInUserinfo = user;
        this.avatar = user.avatar || null;
        this.avatarLoadFailed = false;
        this.syncGlobalNotificationSubscription();
      }
    });
    
    // Subscribe to navigation events to check MFA status on each route change
    this._router.events.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Refresh user info and check MFA on every navigation
        this.getUserInfo();
      }
    });
  }

  toogleTheme() {
    this.isDark = !this.isDark;
    localStorage.setItem('isDark', this.isDark.toString());
    this.setTheme();
  }

  setTheme() {
    var element = this.document.documentElement as HTMLElement;
    if (this.isDark) {
      this._renderer.addClass(element, 'dark');
      this._renderer.removeClass(element, 'light');
    } else {
      this._renderer.addClass(element, 'light');
      this._renderer.removeClass(element, 'dark');
    }
  }

  logout() {
    this._authService.logout();
  }

  public expand(event: any): void {
    if (event) {
      event.stopPropagation();
    }
    let htmlElement = this.document.getElementById('html_element');
    htmlElement.classList.add('layout-menu-expanded');
  }

  private getUserInfo(): void {
    this._userService.getUserInfo().subscribe({
      next: (response: CommonApiResponse<LoggedInUserinfo>) => {
        this.loggedInUserinfo = response.data;
        console.log('[Header notification] user info loaded', {
          contactId: this.resolveContactId(this.loggedInUserinfo),
          contactMethods: this.loggedInUserinfo?.contact_information?.contact_methods
        });
        this._headerService.setUserInfo(this.loggedInUserinfo);
        this.avatarLoadFailed = false;
        this.avatar = this.loggedInUserinfo?.avatar || null;
        this.syncGlobalNotificationSubscription();

        // Check if user needs to activate MFA
        this.checkForceMfaActivation();
      },
      error: err => {
        this.loggedInUserinfo = null;
      }
    });
  }

  private async handleNotificationPopup(notification: any): Promise<void> {
    console.log('[Header notification] opening popup with notification', {
      notification,
      topLevelSubject: notification?.subject,
      topLevelDescription: notification?.description,
      detailsSubject: notification?.details?.subject,
      detailsDescription: notification?.details?.description
    });
    const popupComponent = await this.getAlarmPopupComponent();
    console.log('[Header notification] popup component resolved', {
      hasPopupComponent: !!popupComponent
    });
    popupComponent?.open(notification);
  }

  private syncGlobalNotificationSubscription(): void {
    const contactId = this.resolveContactId(this.loggedInUserinfo);

    if (!contactId) {
      this.notificationContactId = null;
      this.unsubscribeFromGlobalNotifications();
      return;
    }


    this.notificationContactId = contactId;
    this.unsubscribeFromGlobalNotifications();

    const topic = `v1/notifications/${contactId}`;
    this.notificationSubscription = this._mqttService.observe(topic).subscribe({
      next: (message: IMqttMessage) => {
        const payloadText = message.payload.toString();
        const canShowPopup = this.canShowPopupForUser(this.loggedInUserinfo);
        console.log('[Header notification] MQTT message received', {
          topic: message.topic,
          payloadText,
          canShowPopup,
          contactMethods: this.loggedInUserinfo?.contact_information?.contact_methods
        });

        try {
          const payload = JSON.parse(payloadText);
          console.log('[Header notification] parsed MQTT notification payload', {
            payload,
            topLevelSubject: payload?.subject,
            topLevelDescription: payload?.description,
            detailsSubject: payload?.details?.subject,
            detailsDescription: payload?.details?.description
          });
          const notification = { ...payload, _topic: message.topic };
          void this.handleNotificationPopup(notification);
        } catch (error) {}
      },
      error: (error) => {
        console.error('[Header notification] MQTT notification subscription error', error);
      }
    });
  }

  private unsubscribeFromGlobalNotifications(): void {
    try {
      this.notificationSubscription?.unsubscribe();
      this.notificationSubscription = undefined;
    } catch (error) {}
  }

  private resolveContactId(user: LoggedInUserinfo | null | undefined): string | null {
    const contactInformation: any = user?.contact_information;
    return contactInformation?.id || contactInformation?.contact_id || null;
  }

  private async getAlarmPopupComponent(): Promise<{ open(notification: any): void } | null> {
    if (this.alarmPopupComponent) {
      return this.alarmPopupComponent;
    }


    const { AlarmPopupComponent } = await import('../../../feature/alarms/components/alarm-popup/alarm-popup.component');
    this.alarmPopupHost.clear();
    const componentRef = this.alarmPopupHost.createComponent(AlarmPopupComponent);
    this.alarmPopupComponent = componentRef.instance;
    return this.alarmPopupComponent;
  }


  private canShowPopupForUser(user: LoggedInUserinfo | null | undefined): boolean {
    const contactInformation = user?.contact_information;
    if (!contactInformation) return false;

    const methods = contactInformation?.contact_methods;
    if (!Array.isArray(methods) || !methods.length) return false;

    return methods.some((method: any) => {
      const methodName = typeof method === 'string' ? method : method?.name;
      return String(methodName || '').trim().toLowerCase() === 'popup';
    });
  }

  public onAvatarError(): void {
    this.avatarLoadFailed = true;
    this.avatar = null;
  }

  private checkForceMfaActivation(): void {
    // Check if user is forced to activate MFA but hasn't done so yet
    // Don't show if we're already on the account/security page or if modal is already open
    if (!this.isMfaModalOpen && 
        this.loggedInUserinfo?.force_mfa === true && 
        (this.loggedInUserinfo?.mfa_active === false || this.loggedInUserinfo.mfa_active === null) &&
        !this._router.url.includes('/app/account')) {
      
       this.isMfaModalOpen = true;
      
      Swal.fire({
        title: 'MFA Activation Required',
        html: `
          <div class="text-center">
            <p class="mb-3 px-5">Your administrator has required Multi-Factor Authentication (MFA) for your account.</p>
            <h5 class="mb-4 text-danger fw-bold">You must activate MFA to continue using the system.</h5>
            <p class="text-muted small p-0">Click the button below to set up MFA now.</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: 'Activate MFA Now',
        customClass: {
          confirmButton: 'btn btn-primary'
        },
        zIndex: 999999999999,
        buttonsStyling: false
      } as any).then((result) => {
        this.isMfaModalOpen = false;
        if (result.isConfirmed) {
          this._router.navigate(['/app/account'], { queryParams: { tab: 'Security' } });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeFromGlobalNotifications();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // TEST METHOD: Call this to show popup for testing
  public showTestNotification(): void {
    const testNotification = {
      type: 'Alarm',
      message: 'Temperature Alert - Building A',
      description: 'The temperature in Building A has exceeded safe limits and reached 48°C. Immediate action required.',
      subject: 'Temperature Alert - Building A',
      alarm_severity: 'High',
      alarm_configuration_id: 'test-alarm-config-123',
      device_id: 'test-device-456',
      device_name: 'Building A Sensor',
      device_location: 'Building A',
      contact_id: 'contact-001'
    };
    void this.handleNotificationPopup(testNotification);
  }

  getUserInitials(): string {
    const firstNameInitial = this.loggedInUserinfo?.first_name?.charAt(0).toUpperCase() || '';
    const lastNameInitial = this.loggedInUserinfo?.last_name?.charAt(0).toUpperCase() || '';
    return `${firstNameInitial}${lastNameInitial}`;
  }
}
